import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { AuthTokenService, SessionRefreshError } from '../services/auth/auth-token.service';

/**
 * Endpoints whose 401 is NOT "your access token is no longer valid": credential checks (a wrong password
 * must not be retried — it would count twice toward the lockout) and the session endpoints themselves.
 */
const NO_REFRESH_PATHS: readonly string[] = [
  '/api/v1/auth/login',
  '/api/v1/auth/refresh',
  '/api/v1/auth/logout',
  '/api/v1/auth/change-password',
  '/api/v1/auth/google',
  '/api/v1/webauthn/authenticate',
];

const BEARER_PREFIX: string = 'Bearer ';

export function isRefreshExempt(url: string): boolean {
  const path: string = url.split('?')[0];
  return NO_REFRESH_PATHS.some((exempt: string) => path.endsWith(exempt) || path.includes(`${exempt}/`));
}

/**
 * Turns an expired/revoked access token into a transparent refresh:
 * a request that carried a bearer token and got 401 triggers ONE refresh (shared by every request failing
 * at the same time, and serialized across tabs), then is retried once with the new token.
 *
 * - Refresh rejected by the server → the session is cleared (AuthService resets state; the HTTP wrapper
 *   routes to /login) and the original 401 propagates.
 * - Refresh failed for network/5xx reasons → nothing is cleared; the original error propagates.
 * - Retried request still 401 → it is an endpoint-level 401, not a session problem; it propagates as-is.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly tokens = inject(AuthTokenService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authorization: string | null = request.headers.get('Authorization');
    if (!authorization?.startsWith(BEARER_PREFIX) || isRefreshExempt(request.url)) {
      return next.handle(request);
    }
    const sentToken: string = authorization.slice(BEARER_PREFIX.length);

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
          return throwError(() => error);
        }
        return from(this.tokens.refreshAccessToken(sentToken)).pipe(
          catchError((refreshError: unknown) => {
            if (refreshError instanceof SessionRefreshError && refreshError.isDefinitive) {
              this.tokens.expireSession();
            }
            return throwError(() => error);
          }),
          switchMap((freshToken: string) =>
            next.handle(request.clone({ setHeaders: { Authorization: `${BEARER_PREFIX}${freshToken}` } }))
          )
        );
      })
    );
  }
}
